import type { ProblemStatementRuleSnapshot } from "@/services/api/case-checking";
import {
  normalizeRuleStatus,
  type RuleIssueStatus,
} from "@/components/case-checking/problemStatementRuleIssues";

const RULE_DISPLAY_ORDER: Record<RuleIssueStatus, number> = {
  failed: 0,
  unclear: 1,
  passed: 2,
  na: 3,
};

export function sortRulesForDisplay(rules: ProblemStatementRuleSnapshot[]): ProblemStatementRuleSnapshot[] {
  return [...rules].sort((a, b) => {
    const byStatus =
      RULE_DISPLAY_ORDER[normalizeRuleStatus(a.status)] - RULE_DISPLAY_ORDER[normalizeRuleStatus(b.status)];
    if (byStatus !== 0) return byStatus;
    return a.rule_id.localeCompare(b.rule_id);
  });
}

/** Outcomes 1.1 … 7.2 in order, then PSnn (so the PS24 overall sign-off sorts last); unknown ids last, then alphabetically. */
export function compareProblemStatementIds(aId: string, bId: string): number {
  const key = (id: string) => {
    const dotted = id.match(/^(\d+)\.(\d+)$/);
    if (dotted) return Number(dotted[1]) * 1000 + Number(dotted[2]);
    const ps = id.match(/^PS(\d+)$/i);
    return ps ? Number(ps[1]) * 1000 : Number.MAX_SAFE_INTEGER;
  };
  return key(aId) - key(bId) || aId.localeCompare(bId);
}

export type GatewayGroup<T> = { gateway: number | null; label: string | null; items: T[] };

/** Group outcomes under their gateway; a single unlabelled group when the tenant has no gateways. */
export function groupByGateway<T extends { gateway?: number | null; gateway_name?: string }>(items: T[]): GatewayGroup<T>[] {
  if (!items.some((item) => item.gateway != null)) return [{ gateway: null, label: null, items }];
  const groups = new Map<number | null, GatewayGroup<T>>();
  for (const item of items) {
    const gateway = item.gateway ?? null;
    let group = groups.get(gateway);
    if (!group) {
      const label = gateway == null ? null : `Gateway ${gateway}${item.gateway_name ? ` · ${item.gateway_name}` : ""}`;
      group = { gateway, label, items: [] };
      groups.set(gateway, group);
    }
    group.items.push(item);
  }
  return [...groups.values()].sort((a, b) => (a.gateway ?? Number.MAX_SAFE_INTEGER) - (b.gateway ?? Number.MAX_SAFE_INTEGER));
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1).trim()}…`;
}

function humanizeToken(value: string): string {
  return value
    .replace(/-/g, "_")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function inferConsistencyName(text: string): string | null {
  const lower = text.toLowerCase();
  if (lower.includes("net income") || lower.includes("joint income")) return "Income consistency";
  if (lower.includes("affordability") || lower.includes("expenditure")) return "Affordability consistency";
  if (lower.includes("income")) return "Income consistency";
  if (lower.includes("risk profile") || lower.includes("attitude to risk")) return "Risk consistency";
  if (lower.includes("client name")) return "Client details consistency";
  return null;
}

/** Catalog labels that use `/` as a hierarchy, not prose. */
export function ruleLabelBreadcrumb(label: string): string | null {
  if (!label.includes("/")) return null;
  return label
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => humanizeToken(part.replace(/\s+/g, "_")))
    .join(" → ");
}

function shortRuleTitle(label: string, ruleId: string, evidence: string): string {
  const id = ruleId.toLowerCase();
  const breadcrumb = ruleLabelBreadcrumb(label);
  const probe = breadcrumb ? label.replace(/\//g, " ") : label;

  if (id.startsWith("cons-")) {
    return inferConsistencyName(probe || evidence) ?? breadcrumb ?? "Consistency check";
  }

  if (breadcrumb) return breadcrumb;

  return label;
}

/**
 * Human-readable rule name from catalog `label`.
 * Evidence / agent reasoning stays in the evidence field — not used as the title.
 */
export function ruleDisplayName(rule: ProblemStatementRuleSnapshot): string {
  const label = rule.label?.trim() ?? "";
  const evidence = rule.evidence?.trim() ?? "";
  const id = rule.rule_id.toLowerCase();

  if (label.includes("/") || id.startsWith("cons-")) {
    const short = shortRuleTitle(label, rule.rule_id, evidence);
    if (short) return short;
  }

  if (label && evidence && label !== evidence) {
    return label.length <= 120 ? label : truncate(label, 120);
  }

  if (label && label.length <= 120) {
    return label;
  }

  if (id.startsWith("cons-")) {
    return inferConsistencyName(label || evidence) ?? "Consistency check";
  }

  if (label) {
    const firstLine = label.split("\n")[0]?.trim() ?? label;
    if (firstLine.length <= 120) return firstLine;
    if (firstLine.includes("?")) return truncate(firstLine, 120);
  }

  return humanizeToken(rule.rule_id) || "Compliance check";
}

/** One-line label for collapsed rule rows. */
export function ruleRowTitle(rule: ProblemStatementRuleSnapshot, maxLen = 110): string {
  return truncate(ruleDisplayName(rule), maxLen);
}
